import {API_BASE_URL} from '../../../security/ordinarySession';
import {apiFetch} from '../../../security/apiFetch';
import { useState, useCallback,useEffect,useRef } from 'react';
import axios from 'axios';
import { Lesson, WordData, AyahData } from '../types';
import { LETTER_MAPPING, API_ENDPOINTS } from '../constants';



export const useArabicLessonData = (token: string | null) => {
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [words, setWords] = useState<WordData | null>(null);
  const [ayahData, setAyahData] = useState<AyahData | null>(null);
  const [ayahAudioUrl, setAyahAudioUrl] = useState<string | null>(null);
  const [ayahReciter, setAyahReciter] = useState<string | null>(null);
  const [ayahTranslation, setAyahTranslation] = useState<string | null>(null);
  const [ayahTafsir, setAyahTafsir] = useState<string | null>(null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lessonVersion=useRef(0);
  const ayahVersion=useRef(0);
  const wordsVersion=useRef(0);
  useEffect(()=>{const version=ayahVersion,lessonEpoch=lessonVersion,wordsEpoch=wordsVersion;setLesson(null);setWords(null);setError(null);setAyahAudioUrl(null);setAyahReciter(null);setAyahData(null);return()=>{version.current++;lessonEpoch.current++;wordsEpoch.current++;};},[token]);

  // Загрузка урока по букве
  const fetchLesson = useCallback(async (letter: string) => {
    if (!letter || !token) return null;
    const current=++lessonVersion.current;setLesson(null);
    
    try {
      setLoading(true);
      setError(null);
      
      // The API accepts the Arabic scalar. Alias spellings differ between older clients and Gateway.
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.LESSONS}/by-letter/${encodeURIComponent(letter)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if(current!==lessonVersion.current)return null;
      if (!response.data.lesson || typeof response.data.lesson.id!=='string' || !Array.isArray(response.data.lesson.steps) || !response.data.lesson.steps.length) {
        throw new Error('Некорректный ответ API: отсутствует объект lesson');
      }
      
      const lessonData = response.data.lesson;
      setLesson(lessonData);

      
      return lessonData;
      
    } catch (err: any) {

      if(current!==lessonVersion.current)return null;
      setError(err.response?.status===404?'Материал этой буквы пока не загружен на сервер.':'Не удалось загрузить полный урок. Повторите попытку.');
      return null;
    } finally {
      if(current===lessonVersion.current)setLoading(false);
    }
  }, [token]);

  // Загрузка слов для буквы
  const fetchWords = useCallback(async (letter: string) => {
    const current=++wordsVersion.current;setWords(null);
    try {
      if(!letter||!token)throw new Error('Missing letter or session');
      const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.WORDS}/${encodeURIComponent(letter)}/words`, {
        headers: { Authorization: `Bearer ${token}` },timeout:15000
      });
      if(current!==wordsVersion.current)return;
      if(!Array.isArray(response.data?.words))throw new Error('Invalid word catalog');
      setWords(response.data);setError(null);
    } catch {
      if(current===wordsVersion.current)setError('Не удалось загрузить исходные примеры слов.');
    }
  }, [token]);

  // Загрузка аята с буквой
  const fetchAyahWithLetter = useCallback(async (letter: string) => {
    const current=++ayahVersion.current;
    try {
      setLoading(true);
      setError(null);
      setAyahData(null);setAyahAudioUrl(null);setAyahReciter(null);setAyahTranslation(null);setAyahTafsir(null);
      const canonical=LETTER_MAPPING.has(letter)?letter:Array.from(LETTER_MAPPING.entries()).find(([,name])=>name===letter)?.[0];
      if(!token||!canonical)throw new Error('Invalid letter or session');
      const options={headers:{Authorization:`Bearer ${token}`},timeout:15000};
      const response=await axios.get(`${API_BASE_URL}${API_ENDPOINTS.QURAN}/random-ayah-with-letter/${encodeURIComponent(canonical)}`,options);
      if(current!==ayahVersion.current)return;
      const data=response.data;
      if(typeof data?.text_arabic!=='string'||data.letter!==canonical||data.text_index_unit!=='unicode_scalar'||data.match_mode!=='exact_unicode_scalar'||
         typeof data.source!=='string'||!data.source.trim()||!['sampled_surah','full_local_numbered_corpus'].includes(data.selection_scope)||!Number.isInteger(data.total_matching_ayahs)||data.total_matching_ayahs<1||
         !Number.isInteger(data.surah_number)||data.surah_number<1||data.surah_number>114||!Number.isInteger(data.ayah_number)||data.ayah_number<1||data.ayah_number>286||
         !Array.isArray(data.letter_positions)||!Number.isInteger(data.letter_count))throw new Error('Invalid source geometry');
      const positions=Array.from(data.text_arabic as string).flatMap((char,index)=>char===canonical?[index]:[]);
      if(!positions.length||data.letter_count!==positions.length||data.letter_positions.length!==positions.length||
         positions.some((position,index)=>data.letter_positions[index]!==position))throw new Error('Invalid source geometry');
      setAyahData(data);
      const surah=data.surah_number,ayah=data.ayah_number;
      const [audio,verse,tafsir]=await Promise.allSettled([
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.QURAN}/audio/${surah}/${ayah}`,options),
        axios.get(`${API_BASE_URL}${API_ENDPOINTS.QURAN}/verse/${surah}/${ayah}`,options),
        axios.get(`${API_BASE_URL}/api/v1/quran/reference/tafsir/russian_saadi/${surah}/${ayah}`,options),
      ]);
      if(current!==ayahVersion.current)return;
      const metadata=audio.status==='fulfilled'?audio.value.data:null;
      if(metadata?.surah===surah&&metadata?.ayah===ayah&&metadata?.scope==='ayah'&&metadata?.source==='local_import'&&typeof metadata.recitation_id==='string'&&/^[a-z0-9-]{1,80}$/.test(metadata.recitation_id)&&typeof metadata.reciter==='string'&&metadata.reciter.trim()&&metadata.audio_url===`/api/v1/quran/recitations/${metadata.recitation_id}/audio/${surah}/${ayah}`){
        setAyahAudioUrl(metadata.audio_url);setAyahReciter(metadata.reciter);
      }else setError('Аудио этого аята недоступно.');
      if(verse.status==='fulfilled'&&verse.value.data?.surah===surah&&verse.value.data?.ayah===ayah&&typeof verse.value.data?.translation_ru==='string'&&verse.value.data.translation_ru.trim())setAyahTranslation(verse.value.data.translation_ru);
      if(tafsir.status==='fulfilled'&&Array.isArray(tafsir.value.data?.blocks)){
        const blocks=tafsir.value.data.blocks;
        if(blocks.length&&blocks.every((block:any)=>block.sura===surah&&Number.isInteger(block.from_aya)&&Number.isInteger(block.to_aya)&&block.from_aya<=ayah&&ayah<=block.to_aya&&typeof block.text==='string'&&block.text.trim())){
          setAyahTafsir(blocks.map((block:any)=>`Аяты ${block.from_aya}–${block.to_aya}: ${block.text}`).join('\n\n'));
        }
      }
    } catch (err: any) {
      if(current!==ayahVersion.current)return;
      setError(err.response?.status===404?'Аят с указанной буквой не найден.':'Не удалось загрузить аят. Повторите попытку.');
    } finally {
      if(current===ayahVersion.current)setLoading(false);
    }
  }, [token]);

  const loadLetterProgress = useCallback(async () => {
    if (!token) throw new Error('Войдите, чтобы загрузить прогресс.');
    const response = await apiFetch('/api/v1/arabic-lessons/letter-progress', {headers: {Authorization: `Bearer ${token}`}});
    if (!response.ok) throw new Error('Прогресс недоступен.');
    const data = await response.json();
    if(!Array.isArray(data.completed_letters)||data.completed_letters.some((letter:unknown)=>typeof letter!=='string'||!LETTER_MAPPING.has(letter))||(data.current_letter!==null&&data.current_letter!==undefined&&(typeof data.current_letter!=='string'||!LETTER_MAPPING.has(data.current_letter))))throw new Error('Некорректный прогресс букв.');
    const progress = new Map<string, 'not_started' | 'in_progress' | 'completed'>();
    for (const letter of data.completed_letters) progress.set(letter, 'completed');
    if (data.current_letter && !progress.has(data.current_letter)) progress.set(data.current_letter, 'in_progress');
    return progress;
  }, [token]);

  const saveLessonProgress = useCallback(async (lessonId: string, progress: {status: 'completed' | 'in_progress'; time_spent?: number; step_results?: Record<string, unknown>}) => {
    const current = lessonVersion.current;
    try {
      if (!token) throw new Error('Войдите, чтобы сохранить прогресс.');
      const response = await apiFetch(`/api/v1/arabic-lessons/lessons/${lessonId}/progress`, {
        method: 'POST', headers: {Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'},
        body: JSON.stringify({status: progress.status, time_spent: progress.time_spent, step_results: progress.step_results})
      });
      if (!response.ok) throw new Error('Сохранение прогресса не подтверждено сервером.');
      const data = await response.json();
      if(current===lessonVersion.current)setError(null);
      return data;
    } catch (err) {
      if(current===lessonVersion.current)setError('Сохранение прогресса не подтверждено сервером.');
      throw err;
    }
  }, [token]);

  // Очистка данных
  const clearData = useCallback(() => {
    ayahVersion.current++;lessonVersion.current++;wordsVersion.current++;setLoading(false);
    setLesson(null);
    setWords(null);
    setAyahData(null);
    setAyahAudioUrl(null);
    setAyahReciter(null);
    setAyahTranslation(null);
    setAyahTafsir(null);
    setError(null);
  }, []);

  return {
    // Данные
    lesson,
    words,
    ayahData,
    ayahAudioUrl,
    ayahReciter,
    ayahTranslation,
    ayahTafsir,
    
    // Состояние
    loading,
    error,
    
    // Методы
    loadLessonData: fetchLesson,
    loadWords: fetchWords,
    loadAyah: fetchAyahWithLetter,
    loadLetterProgress,
    saveLessonProgress,
    clearData,
    clearError:()=>setError(null)
  };
};
